import { useLocation, useSearchParams } from '@remix-run/react';
import type { Coords } from 'google-map-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
    getArchiProMapVariables,
    DESKTOP_MAP_TOP_GAP,
    MOBILE_MAP_TOP_GAP,
    SCROLL_TO_MAP_AFTER_CLICK_LINK,
} from '@modules/professional/component/contact-tab/LocationConstant';
import {
    locationHasDistance,
    useLocationsSortByDistance,
} from '@modules/professional/hook/use-locations-sort-by-distance';
import {
    getInitialContactID,
    scrollToRefTop,
} from '../util/contact-page-helper';
import type {
    LocationDataType,
    LocationStateType,
} from '../type/contact-page-types';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { useRootData } from '@modules/root/hook/use-root-data';

export const useContactLocationMap = (
    originalLocations: LocationDataType[],
    locationListSectionRef?: React.RefObject<HTMLDivElement>,
    skip?: boolean,
    professionalCountry?: string
) => {
    const [searchParams] = useSearchParams();
    const urlLocation = useLocation();

    const {
        localisation: { country },
    } = useRootData();
    const { mobile: isMobile } = useAppDisplayModeContext();

    const { sortedLocations, calculateDistance } =
        useLocationsSortByDistance<LocationDataType>({
            locations: originalLocations,
            skip,
        });

    const hasDistance = locationHasDistance(sortedLocations);

    const initialID = getInitialContactID(
        urlLocation.state as LocationStateType,
        searchParams
    );
    const initialLocation = initialID
        ? sortedLocations.find((l: LocationDataType) => l.ID === initialID)
        : sortedLocations[0];

    const [filteredLocations, setFilteredLocations] =
        useState<LocationDataType[]>(sortedLocations);
    const [hoveredLocation, setHoveredLocation] = useState<LocationDataType>();
    const [clickedLocation, setClickedLocation] = useState<
        LocationDataType | undefined
    >(initialLocation);

    const mapVariables = getArchiProMapVariables(
        professionalCountry || country,
        isMobile
    );
    const defaultZoomNum =
        sortedLocations.length === 1 || clickedLocation
            ? mapVariables.zoom.click
            : mapVariables.zoom.min;
    const defaultCenter = clickedLocation?.MapCoordinates
        ? {
              lat: clickedLocation.MapCoordinates.Latitude,
              lng: clickedLocation.MapCoordinates.Longitude,
          }
        : mapVariables.center;
    const [mapZoomNum, setMapZoomNum] = useState(defaultZoomNum);
    const [mapCenter, setMapCenter] = useState<Coords>(defaultCenter);

    const popupCloseRef = useRef(false);

    // Memoize changeUrlWithoutReload to prevent recreation on every render
    const changeUrlWithoutReload = useCallback(
        (ID?: number) => {
            if (history?.pushState) {
                const newRelativePathQuery = ID
                    ? urlLocation.pathname + '?ID=' + String(ID)
                    : urlLocation.pathname;
                history.pushState(null, '', newRelativePathQuery);
            }
        },
        [urlLocation.pathname]
    );

    // Memoize onLocationDetailClose to prevent recreation
    const onLocationDetailClose = useCallback(() => {
        setHoveredLocation(undefined);
        setClickedLocation(undefined);
        changeUrlWithoutReload();
    }, [changeUrlWithoutReload]);

    // Memoize onSearchChange to prevent recreation
    const onSearchChange = useCallback(
        (searchKeyword = '') => {
            const newLocations = !searchKeyword
                ? sortedLocations
                : sortedLocations.filter((obj) => {
                      return Object.values(obj)
                          .toString()
                          .toLowerCase()
                          .includes(searchKeyword.toLowerCase());
                  });
            setFilteredLocations(newLocations);
            onLocationDetailClose();
        },
        [sortedLocations, onLocationDetailClose]
    );

    // Memoize onSearchClick to prevent recreation
    const onSearchClick = useCallback(
        (searchKeyword = '') => {
            searchKeyword === '' && onLocationDetailClose();
        },
        [onLocationDetailClose]
    );

    // Memoize onListItemClick to prevent recreation
    const onListItemClick = useCallback(
        (location: LocationDataType) => {
            setHoveredLocation(location);
            setClickedLocation(location);
            locationListSectionRef &&
                scrollToRefTop(
                    locationListSectionRef,
                    isMobile ? undefined : 120
                );
            changeUrlWithoutReload(location.ID);
        },
        [locationListSectionRef, isMobile, changeUrlWithoutReload]
    );

    // Memoize onListItemHover to prevent recreation
    const onListItemHover = useCallback((location: LocationDataType) => {
        setHoveredLocation(location);
    }, []);

    // Memoize onMapMarkerHover to prevent recreation
    const onMapMarkerHover = useCallback((locationData: LocationDataType) => {
        setHoveredLocation(locationData);
    }, []);

    // Memoize onMapMarkerClick to prevent recreation
    const onMapMarkerClick = useCallback(
        (locationData: LocationDataType) => {
            if (locationData) {
                setHoveredLocation(locationData);
            }
            setClickedLocation(locationData);

            const offset = isMobile ? MOBILE_MAP_TOP_GAP : DESKTOP_MAP_TOP_GAP;

            locationListSectionRef &&
                scrollToRefTop(locationListSectionRef, offset);
        },
        [isMobile, locationListSectionRef]
    );

    const setMapCenterAndZoom = useCallback(
        (
            locationData: LocationDataType | null,
            zoom: number = mapVariables.zoom.search,
            latitudeOffset = mapVariables.latitudeOffset
        ) => {
            let newMapCenter = mapVariables.center;
            setMapZoomNum(zoom);
            if (locationData && locationData.MapCoordinates) {
                newMapCenter = {
                    lat: locationData.MapCoordinates.Latitude + latitudeOffset,
                    lng: locationData.MapCoordinates.Longitude,
                };
            } else if (!locationData) {
                setMapZoomNum(mapVariables.zoom.min);
            }

            // re-center map after map-zoom changed
            setTimeout(() => {
                setMapCenter(newMapCenter);
            }, 500); // Note: too small may cause map not re-center
        },
        [mapVariables]
    );

    // Memoize onMapPopupClose to prevent recreation
    const onMapPopupClose = useCallback(() => {
        setHoveredLocation(undefined);
        setClickedLocation(undefined);
        popupCloseRef.current = true;
    }, []);

    // Open location detail section for nearest location
    useEffect(() => {
        if (hasDistance) {
            setFilteredLocations(sortedLocations);
            if (!initialLocation) {
                setClickedLocation(sortedLocations[0]);
            }
        }
        if (initialLocation && SCROLL_TO_MAP_AFTER_CLICK_LINK) {
            locationListSectionRef && scrollToRefTop(locationListSectionRef);
        }
    }, [sortedLocations, hasDistance, initialLocation, locationListSectionRef]);

    // If professional only has one location: Zoom and centre on it
    useEffect(() => {
        if (
            sortedLocations.length === 1 &&
            !clickedLocation &&
            !popupCloseRef.current
        ) {
            setClickedLocation(sortedLocations[0]);
        }
    }, [sortedLocations, clickedLocation]);

    // re-center map and zoom when location is clicked
    useEffect(() => {
        if (clickedLocation) {
            const zoom =
                sortedLocations.length === 1
                    ? mapVariables.zoom.min
                    : mapVariables.zoom.click;
            setMapCenterAndZoom(clickedLocation, zoom);
        } else {
            setMapCenterAndZoom(null, mapVariables.zoom.search);
        }
    }, [clickedLocation, mapVariables, setMapCenterAndZoom, sortedLocations]);

    return {
        onSearchChange,
        onSearchClick,
        onMapMarkerHover,
        onMapMarkerClick,
        onMapPopupClose,
        onLocationDetailClose,
        onListItemClick,
        onListItemHover,
        changeUrlWithoutReload,
        calculateDistance,
        mapCenter,
        mapZoomNum,
        clickedLocation,
        hoveredLocation,
        filteredLocations,
        sortedLocations,
        hasDistance,
    };
};
